import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Layer } from "../types/Editor";
import {
  Droplet,
  EyeOff,
  Highlighter,
  Palette,
  Sliders,
  Trash2,
  ZoomIn,
  Target,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface LayerSettingsProps {
  activeLayer: Layer | null;
  onLayerUpdate: (layer: Layer) => void;
  onLayerDelete: (layerId: string) => void;
  videoSrc?: string | null;
  onLayerFocus?: (layer: Layer) => void;
}

export function LayerSettings({
  activeLayer,
  onLayerUpdate,
  onLayerDelete,
  videoSrc,
  onLayerFocus,
}: LayerSettingsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDraggingTarget, setIsDraggingTarget] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);

  // Load video frame for zoom preview
  useEffect(() => {
    if (
      !activeLayer ||
      activeLayer.type !== "zoom" ||
      !videoSrc ||
      !canvasRef.current
    ) {
      setFrameLoaded(false);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.src = videoSrc;

    const drawFrame = () => {
      video.currentTime = activeLayer.start;
    };

    video.addEventListener("loadedmetadata", drawFrame);

    video.addEventListener("seeked", () => {
      // Set canvas dimensions to match preview area
      canvas.width = 300;
      canvas.height = 300 / (video.videoWidth / video.videoHeight);

      // Draw the video frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setFrameLoaded(true);
    });

    return () => {
      video.removeEventListener("loadedmetadata", drawFrame);
      video.src = "";
    };
  }, [activeLayer, videoSrc]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!activeLayer || activeLayer.type !== "zoom" || !canvasRef.current)
      return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    onLayerUpdate({
      ...activeLayer,
      data: {
        ...activeLayer.data,
        centerX: x,
        centerY: y,
      },
    });
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!activeLayer || activeLayer.type !== "zoom") return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    const centerX = activeLayer.data.centerX ?? 0.5;
    const centerY = activeLayer.data.centerY ?? 0.5;

    const distance = Math.sqrt(
      Math.pow((x - centerX) * rect.width, 2) +
      Math.pow((y - centerY) * rect.height, 2)
    );

    if (distance < 30) {
      setIsDraggingTarget(true);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDraggingTarget || !activeLayer || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    onLayerUpdate({
      ...activeLayer,
      data: {
        ...activeLayer.data,
        centerX: x,
        centerY: y,
      },
    });
  };

  const handleCanvasMouseUp = () => {
    setIsDraggingTarget(false);
  };

  if (!activeLayer) {
    return (
      <div className="w-64 rounded-3xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col items-center justify-center text-center">
        <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
          <Sliders className="h-6 w-6 text-slate-300" />
        </div>
        <h3 className="text-sm font-bold text-slate-900 mb-2">Layer Settings</h3>
        <p className="text-slate-500 text-xs leading-relaxed">
          Select a layer in your timeline to view and edit its properties.
        </p>
      </div>
    );
  }

  const theme = {
    highlight: {
      chip: "bg-amber-50 text-amber-700 border-amber-100",
      icon: <Highlighter size={12} />,
      accent: "indigo",
    },
    blur: {
      chip: "bg-purple-50 text-purple-700 border-purple-100",
      icon: <EyeOff size={12} />,
      accent: "indigo",
    },
    zoom: {
      chip: "bg-indigo-50 text-indigo-700 border-indigo-100",
      icon: <ZoomIn size={12} />,
      accent: "indigo",
    },
  }[activeLayer.type];

  return (
    <div
      className="w-64 rounded-3xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col h-full overflow-hidden"
      onPointerDown={() => {
        if (activeLayer && onLayerFocus) onLayerFocus(activeLayer);
      }}
    >
      {/* HEADER */}
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Settings</h2>
          <div className={`flex items-center gap-2 rounded-full text-[10px] font-bold uppercase tracking-wider pl-2.5 pr-1.5 py-1 border ${theme.chip}`}>
            <div className="flex items-center gap-1.5">
              {theme.icon}
              <span>{activeLayer.type}</span>
            </div>
            <div className="w-px h-3 bg-current opacity-20 mx-0.5" />
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLayerDelete(activeLayer.id);
              }}
              className="p-1 hover:text-rose-600 transition-colors rounded-full hover:bg-white/50"
              title="Remove Layer"
            >
              <Trash2 size={12} strokeWidth={2.5} />
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">Properties</p>
      </div>

      {/* FIELDS */}
      <div className="space-y-6 flex-grow overflow-y-auto custom-scrollbar pr-1">
        {/* Highlight Settings */}
        {activeLayer.type === "highlight" && (
          <>
            <SettingRow icon={<Palette size={14} />} label="Color">
              <div className="relative w-10 h-10 rounded-xl border border-slate-200 overflow-hidden cursor-pointer">
                <Input
                  type="color"
                  value={activeLayer.data.color || "#4f46e5"}
                  onChange={(e) =>
                    onLayerUpdate({
                      ...activeLayer,
                      data: {
                        ...activeLayer.data,
                        color: e.target.value,
                      },
                    })
                  }
                  className="absolute inset-[-4px] w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer p-0 border-0"
                />
              </div>
            </SettingRow>

            <div className="space-y-3">
              <Label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Sliders size={12} />
                Opacity ({activeLayer.data.transparency}%)
              </Label>
              <input
                type="range"
                min="0"
                max="100"
                value={activeLayer.data.transparency || 100}
                onChange={(e) =>
                  onLayerUpdate({
                    ...activeLayer,
                    data: {
                      ...activeLayer.data,
                      transparency: parseInt(e.target.value),
                    },
                  })
                }
                className="w-full accent-indigo-600 h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer"
              />
            </div>
          </>
        )}

        {/* Zoom Settings */}
        {activeLayer.type === "zoom" && (
          <>
            <SettingRow
              icon={<ZoomIn size={14} />}
              label="Scale"
            >
              <Input
                type="number"
                step="0.1"
                value={activeLayer.data.zoomFactor || 1.5}
                onChange={(e) =>
                  onLayerUpdate({
                    ...activeLayer,
                    data: {
                      ...activeLayer.data,
                      zoomFactor: parseFloat(e.target.value),
                    },
                  })
                }
                className="w-16 h-9 rounded-lg border-slate-200 text-xs font-bold"
              />
            </SettingRow>

            {/* Zoom Target Preview */}
            <div className="space-y-3">
              <Label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                <Target size={12} />
                Focus Point
              </Label>
              <div className="relative border border-slate-200 rounded-2xl overflow-hidden bg-slate-900 group shadow-lg">
                <canvas
                  ref={canvasRef}
                  className="w-full cursor-crosshair transition-opacity duration-300"
                  onClick={handleCanvasClick}
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                />
                {frameLoaded && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: `${(activeLayer.data.centerX ?? 0.5) * 100}%`,
                      top: `${(activeLayer.data.centerY ?? 0.5) * 100}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <div className="relative w-8 h-8">
                      <div className="absolute inset-0 rounded-full border-2 border-indigo-400 bg-indigo-400/20 shadow-[0_0_15px_rgba(129,140,248,0.5)]" />
                      <div className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-indigo-400 transform -translate-x-1/2 -translate-y-1/2" />
                    </div>
                  </div>
                )}
                {!frameLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-400 text-[10px] font-bold uppercase tracking-wider">
                    Loading preview...
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-400 font-medium italic text-center">
                Drag the target to adjust focal point
              </p>
            </div>
          </>
        )}

        {/* Blur Settings */}
        {activeLayer.type === "blur" && (
          <div className="space-y-4">
            <SettingRow
              icon={<Droplet size={14} />}
              label="Blur Radius"
            >
              <Input
                type="number"
                value={activeLayer.data.blurAmount || 10}
                onChange={(e) =>
                  onLayerUpdate({
                    ...activeLayer,
                    data: {
                      ...activeLayer.data,
                      blurAmount: parseFloat(e.target.value),
                    },
                  })
                }
                className="w-16 h-9 rounded-lg border-slate-200 text-xs font-bold"
              />
            </SettingRow>
            <input
              type="range"
              min="1"
              max="50"
              value={activeLayer.data.blurAmount || 10}
              onChange={(e) =>
                onLayerUpdate({
                  ...activeLayer,
                  data: {
                    ...activeLayer.data,
                    blurAmount: parseInt(e.target.value),
                  },
                })
              }
              className="w-full accent-purple-600 h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SettingRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label className="flex items-center gap-2 text-slate-700 text-sm font-bold">
        {icon}
        {label}
      </Label>
      {children}
    </div>
  );
}
